import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';
import { useTranslations } from '../../../hooks/use-translations';

const ENTITY_ACTIVITY = 'Activity';
const ENTITY_STATE = 'StateTracking';
const ENTITY_ANOMALY = 'ActivityAnomaly';

function formatCell(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toISOString().replace('T', ' ').slice(0, 19);
      }
    } catch {
      /* ignore */
    }
  }
  return String(value);
}

const ACTIVITY_COLUMNS = [
  { key: 'id', labelKey: 'pipelineColId' },
  { key: 'profileId', labelKey: 'pipelineColProfileId' },
  { key: 'profileFullName', labelKey: 'pipelineColProfileFullName' },
  { key: 'zoneId', labelKey: 'pipelineColZoneId' },
  { key: 'zoneName', labelKey: 'pipelineColZoneName' },
  { key: 'doorId', labelKey: 'pipelineColDoorId' },
  { key: 'activityType', labelKey: 'pipelineColActivityType' },
  { key: 'eventTimeUtc', labelKey: 'pipelineColEventTimeUtc' },
  { key: 'timeInUtc', labelKey: 'pipelineColTimeInUtc' },
  { key: 'timeOutUtc', labelKey: 'pipelineColTimeOutUtc' },
  { key: 'doorControllerUserId', labelKey: 'pipelineColDoorControllerUserId' },
  { key: 'hash', labelKey: 'pipelineColHash' },
];

const STATE_COLUMNS = [
  { key: 'id', labelKey: 'pipelineColId' },
  { key: 'profileId', labelKey: 'pipelineColProfileId' },
  { key: 'profileFullName', labelKey: 'pipelineColProfileFullName' },
  { key: 'zoneId', labelKey: 'pipelineColZoneId' },
  { key: 'zoneName', labelKey: 'pipelineColZoneName' },
  { key: 'doorId', labelKey: 'pipelineColDoorId' },
  { key: 'onSite', labelKey: 'pipelineColOnSite' },
  { key: 'lastInUtc', labelKey: 'pipelineColLastInUtc' },
  { key: 'lastOutUtc', labelKey: 'pipelineColLastOutUtc' },
  { key: 'timeOnSite', labelKey: 'pipelineColTimeOnSite' },
  { key: 'hasAnomalies', labelKey: 'pipelineColHasAnomalies' },
  { key: 'doorControllerUserId', labelKey: 'pipelineColDoorControllerUserId' },
  { key: 'hash', labelKey: 'pipelineColHash' },
];

const ANOMALY_COLUMNS = [
  { key: 'id', labelKey: 'pipelineColId' },
  { key: 'profileActivityId', labelKey: 'pipelineColProfileActivityId' },
  { key: 'profileId', labelKey: 'pipelineColProfileId' },
  { key: 'anomalyName', labelKey: 'pipelineColAnomalyName' },
  { key: 'trackingDay', labelKey: 'pipelineColTrackingDay' },
  { key: 'hash', labelKey: 'pipelineColHash' },
];

function columnsForEntity(entityType) {
  if (entityType === ENTITY_ACTIVITY) {
    return ACTIVITY_COLUMNS;
  }
  if (entityType === ENTITY_STATE) {
    return STATE_COLUMNS;
  }
  return ANOMALY_COLUMNS;
}

function rowsFromPayload(payload, entityType) {
  if (!payload) {
    return { left: [], right: [] };
  }
  if (entityType === ENTITY_ACTIVITY) {
    return {
      left: payload.profileActivityRows ?? [],
      right: payload.faceScannerActivityRows ?? [],
    };
  }
  if (entityType === ENTITY_STATE) {
    return {
      left: payload.profileStateRows ?? [],
      right: payload.faceScannerStateRows ?? [],
    };
  }
  return {
    left: payload.profileAnomalyRows ?? [],
    right: payload.faceScannerAnomalyRows ?? [],
  };
}

export default function PipelineSideBySideSection({ accountId, siteId, accessToken, compact = false }) {
  const { t } = useTranslations();
  const { enqueueSnackbar } = useSnackbar();
  const [trackingDay, setTrackingDay] = useState(() => dayjs());
  const [entityType, setEntityType] = useState(ENTITY_ACTIVITY);
  const [sortField, setSortField] = useState('ProfileId');
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const sortOptions = useMemo(() => {
    if (entityType === ENTITY_ACTIVITY) {
      return [
        { value: 'ProfileId', labelKey: 'pipelineSortProfileId' },
        { value: 'EventTimeUtc', labelKey: 'pipelineSortEventTimeUtc' },
      ];
    }
    if (entityType === ENTITY_STATE) {
      return [
        { value: 'ProfileId', labelKey: 'pipelineSortProfileId' },
        { value: 'ZoneId', labelKey: 'pipelineSortZoneId' },
      ];
    }
    return [
      { value: 'ProfileId', labelKey: 'pipelineSortProfileId' },
      { value: 'AnomalyName', labelKey: 'pipelineSortAnomalyName' },
    ];
  }, [entityType]);

  const handleEntityChange = (next) => {
    setEntityType(next);
    setSortField('ProfileId');
    setPayload(null);
  };

  const handleLoad = async () => {
    if (!accessToken || !accountId || !siteId) {
      return;
    }
    const dayStr = trackingDay && trackingDay.isValid() ? trackingDay.format('YYYY-MM-DD') : '';
    if (!dayStr) {
      enqueueSnackbar(t('sites.administrative.pipelineLoadingFailed'), { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const res = await cloudApi.getSiteProfilePipelineSideBySide(accessToken, {
        accountId,
        siteId,
        trackingDay: dayStr,
        entityType,
        sortField,
      });
      setPayload(res.data);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('sites.administrative.pipelineLoadingFailed'), { variant: 'error' });
      setPayload(null);
    }
    setLoading(false);
  };

  const columns = columnsForEntity(entityType);
  const { left, right } = rowsFromPayload(payload, entityType);

  const renderTable = (titleKey, rows, truncated) => (
    <Paper variant="outlined" sx={{ p: 1, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ px: 1, pt: 1, pb: 0.5 }}>
        {t(`sites.administrative.${titleKey}`)}
      </Typography>
      {truncated && (
        <Alert severity="warning" sx={{ m: 1 }}>
          {t(
            titleKey === 'pipelineProfileTableTitle'
              ? 'sites.administrative.pipelineTruncatedProfile'
              : 'sites.administrative.pipelineTruncatedFaceScanner',
          )}
        </Alert>
      )}
      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key}>{t(`sites.administrative.${col.labelKey}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    {t('sites.administrative.pipelineNoRows')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id ?? `${row.profileId}-${row.profileActivityId}-${row.hash}`}>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ whiteSpace: 'nowrap', maxWidth: 280 }}>
                      {formatCell(row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {!compact && (
        <>
          <Typography variant="h6" gutterBottom>
            {t('sites.administrative.pipelineSideBySideTitle')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('sites.administrative.pipelineSideBySideIntro')}
          </Typography>
        </>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-end">
        <Grid item xs={12} md={3}>
          <DatePicker
            label={t('sites.administrative.pipelineDayLabel')}
            value={trackingDay}
            onChange={(v) => setTrackingDay(v)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="pipeline-entity-label">{t('sites.administrative.pipelineEntityLabel')}</InputLabel>
            <Select
              labelId="pipeline-entity-label"
              label={t('sites.administrative.pipelineEntityLabel')}
              value={entityType}
              onChange={(e) => handleEntityChange(e.target.value)}
            >
              <MenuItem value={ENTITY_ACTIVITY}>{t('sites.administrative.pipelineEntityActivity')}</MenuItem>
              <MenuItem value={ENTITY_STATE}>{t('sites.administrative.pipelineEntityState')}</MenuItem>
              <MenuItem value={ENTITY_ANOMALY}>{t('sites.administrative.pipelineEntityAnomaly')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="pipeline-sort-label">{t('sites.administrative.pipelineSortLabel')}</InputLabel>
            <Select
              labelId="pipeline-sort-label"
              label={t('sites.administrative.pipelineSortLabel')}
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(`sites.administrative.${opt.labelKey}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <LoadingButton
            variant="contained"
            loading={loading}
            fullWidth
            startIcon={<Icon icon="mdi:compare" />}
            onClick={handleLoad}
          >
            {t('sites.administrative.pipelineLoadButton')}
          </LoadingButton>
        </Grid>
      </Grid>

      {payload && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('sites.administrative.pipelineSortApplied', { field: payload.sortFieldApplied ?? '' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('sites.administrative.pipelineRowCap', { cap: payload.rowCap ?? 0 })}
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          {renderTable('pipelineProfileTableTitle', left, Boolean(payload?.truncatedProfile))}
        </Grid>
        <Grid item xs={12} lg={6}>
          {renderTable('pipelineFaceScannerTableTitle', right, Boolean(payload?.truncatedFaceScanner))}
        </Grid>
      </Grid>
    </Paper>
  );
}
